// Transformaciones puras para ReporteTransacciones.vue.
// Reciben los documentos crudos de Firestore y devuelven filas planas listas
// para tabla/exportación. No tocan estado reactivo: el componente congela
// (Object.freeze) los resultados para que Vue no los observe.

const SIN_MESERO = Object.freeze({ Nombre: "" });

export function sortByFolio(a, b) {
  if (b.Folio > a.Folio) return -1;
  if (b.Folio < a.Folio) return 1;
  return 0;
}

// ctx: { currency, filtrarPagos, clavesPermitidas:Set, clientes:Set|null,
//        pagosNombres:Map, meseros:Map }
function makeVisible(ctx) {
  return (clave, clienteId) => {
    if (ctx.filtrarPagos && !ctx.clavesPermitidas.has(clave)) return false;
    if (ctx.clientes && !ctx.clientes.has(clienteId)) return false;
    return true;
  };
}

function resolveMesero(venta, meseros) {
  let id;
  // el último producto del catálogo define el mesero (igual que antes)
  for (const key in venta.Catalogo) {
    id = venta.Catalogo[key]?.ExtraParams?.AYB?.Mesero;
  }
  return meseros.get(id) || SIN_MESERO;
}

function nombrePago(ctx, id) {
  return ctx.pagosNombres.get(id) ?? id;
}

export function buildVentaRows(ventas, ctx) {
  const visible = makeVisible(ctx);
  const rows = [];

  ventas.forEach((venta) => {
    const cliente = venta.Cliente || {};
    let propsDefault = 0;
    let propsUSD = 0;
    const props = Object.entries(venta.Props || {});
    props.forEach(([key, prop]) => {
      const moneda = key.split("_")[1];
      if (moneda == ctx.currency) propsDefault += prop.Cantidad;
      else if (moneda == "USD") propsUSD += prop.Cantidad;
    });

    const base = {
      Folio: venta.Folio,
      FechaVenta: venta.FechaVenta,
      FechaCreacion: venta.FechaCreacion,
      Cliente: cliente,
      Cajero: venta.Cajero,
      Total: venta.Total,
      Devos: venta.Devos,
      PropinaTotal: propsDefault,
      PropinaUSD: propsUSD,
      Mesero: resolveMesero(venta, ctx.meseros),
      venta, // documento original, para el detalle
    };
    const push = (extra) =>
      rows.push({ ...base, ...extra, show: visible(extra.clave, cliente.Id) });

    props.forEach(([key, prop]) => {
      const [pagoId, moneda] = key.split("_");
      push({
        prop: true,
        IdTran: "",
        TipoPago: nombrePago(ctx, pagoId) + " " + moneda + " Propina",
        Cantidad: prop.Cantidad,
        moneda,
        clave: pagoId + "_" + moneda,
      });
    });

    const cambio = venta.Cambio?.[ctx.currency];
    if (cambio > 0) {
      push({
        camb: true,
        IdTran: "",
        TipoPago: "Cambio " + ctx.currency,
        Cantidad: -1 * Number(parseFloat(cambio).toFixed(2)),
        moneda: ctx.currency,
        clave: ctx.currency,
      });
    }

    Object.entries(venta.Pagos || {}).forEach(([clave, pago]) => {
      push({
        IdTran: pago.IdTran,
        TipoPago: pago.TipoPago,
        Cantidad: pago.Cantidad,
        moneda: pago.Moneda,
        clave,
      });
    });
  });

  rows.sort(sortByFolio);
  rows.forEach((r, i) => (r._key = "v" + i));
  return rows;
}

export function buildDevolucionRows(devoluciones, ctx) {
  const visible = makeVisible(ctx);
  const rows = [];

  devoluciones.forEach((dev) => {
    const cliente = dev.Cliente || {};
    let propsDefault = 0;
    let propsUSD = 0;
    const props = Object.entries(dev.Depr || {});
    props.forEach(([key, cantidad]) => {
      const moneda = key.split("_")[1];
      if (moneda == ctx.currency) propsDefault += cantidad;
      else if (moneda == "USD") propsUSD += cantidad;
    });

    const base = {
      esDevolucion: true,
      Folio: dev.Folio,
      Fecha: dev.Fecha,
      Crat: dev.Crat,
      Cliente: cliente,
      Cajero: dev.Cajero,
      PropinaTotal: propsDefault,
      PropinaUSD: propsUSD,
    };
    const push = (extra) =>
      rows.push({ ...base, ...extra, show: visible(extra.clave, cliente.Id) });

    // las propinas sólo se listan si la devolución trae pagos (Devs), como antes
    if (dev.Devs) {
      props.forEach(([key, cantidad]) => {
        const [pagoId, moneda] = key.split("_");
        push({
          prop: true,
          IdTran: "",
          TipoPago: nombrePago(ctx, pagoId) + " " + moneda + " Propina",
          Cantidad: cantidad,
          moneda,
          clave: pagoId + "_" + moneda,
        });
      });
    }

    Object.entries(dev.Devs || {}).forEach(([clave, cantidad]) => {
      const [pagoId, moneda] = clave.split("_");
      push({
        IdTran: "",
        TipoPago: nombrePago(ctx, pagoId),
        Cantidad: cantidad,
        moneda,
        clave,
      });
    });
  });

  rows.sort(sortByFolio);
  rows.forEach((r, i) => (r._key = "d" + i));
  return rows;
}

// Totales por cajero y método de pago. ventasDocs/devsDocs son los documentos
// crudos; ventaRows/devRows las filas ya construidas.
export function buildContadores(ventaRows, devRows, ventasDocs, devsDocs, currency) {
  const dataContador = {};
  const encabezados = {};
  const totales = { Cambio: 0 };

  const cajero = (id) => {
    if (!dataContador[id]) dataContador[id] = { Cambio: 0 };
    return dataContador[id];
  };
  const add = (id, clave, monto) => {
    const c = cajero(id);
    c[clave] = (c[clave] || 0) + monto;
    totales[clave] = (totales[clave] || 0) + monto;
  };

  ventaRows.forEach((v) => {
    if (v.prop || v.camb) return;
    cajero(v.Cajero);
    encabezados[v.clave] = true;
    add(v.Cajero, v.clave, v.Cantidad);
  });

  devRows.forEach((d) => {
    if (d.prop) return;
    add(d.Cajero, d.clave, -d.Cantidad);
  });

  ventasDocs.forEach((vnt) => {
    Object.entries(vnt.Props || {}).forEach(([clave, prop]) => {
      encabezados[clave] = true;
      add(vnt.Cajero, clave, prop.Cantidad);
    });
    const cambio = vnt.Cambio?.[currency];
    if (vnt.Cambio && dataContador[vnt.Cajero]) {
      dataContador[vnt.Cajero].Cambio += cambio || 0;
      totales.Cambio += cambio || 0;
    }
  });

  devsDocs.forEach((dev) => {
    Object.entries(dev.Depr || {}).forEach(([clave, monto]) => {
      add(dev.Cajero, clave, -monto);
    });
  });

  return { dataContador, encabezados, totales };
}
